import {
  BaseFormElementData,
  BaseFormElementModel,
  BaseFormElementSettingsData,
  BaseFormElementState
} from '@/components/addons/registration/BaseFormElementModel';
import { FieldType } from '@/typings/enums/enums';
import { validate } from 'vee-validate';

interface FormElementTextSettingsData extends BaseFormElementSettingsData {
  country_codes_enabled?: string;
  country_codes_tags?: string;
}

export interface FormElementTextData extends BaseFormElementData {
  settings: FormElementTextSettingsData;
}

export interface FormElementTextValue {
  countryCode?: string;
  input?: string;
}

export interface FormElementTextState extends BaseFormElementState<FormElementTextValue> {
  country: {
    codesEnabled?: boolean;
    codeTags?: string[];
  };
}

export class FormElementTextModel extends BaseFormElementModel<
  FormElementTextValue,
  FormElementTextData,
  FormElementTextState
> {
  parseFormElement(data: FormElementTextData) {
    const state = this.state;

    state.country = state.country ?? {};
    if (data.settings && data.settings.country_codes_enabled) {
      state.country.codesEnabled = data.settings.country_codes_enabled === '1';
    } else {
      state.country.codesEnabled = false;
    }
    if (data.settings && data.settings.country_codes_tags) {
      state.country.codeTags = data.settings.country_codes_tags.split(',');
    } else {
      state.country.codeTags = undefined;
    }
  }

  getStringifiedValue(): string | undefined {
    if (this.state.value?.input) {
      return `${this.state.value.countryCode ? this.state.value.countryCode : ''}${this.state.value.input}`;
    }

    return undefined;
  }

  /**
   * Validate the current states value.
   * added validate locally to look up on input instead of getSerializedPostValue()
   * had issue with phone number with countrycode value was +968 and would try and validate that together with the number as value +96823212321
   * this would then break the validation for number and min / max characters -> now we only check the input etc phone number 22334455
   */
  public async validate(): Promise<boolean> {
    if (this.state.ignoreValidation) {
      this.state.isValid = true;
      this.state.validated = true;
      return true;
    }

    if (this.state.containsEncryptedValue) {
      this.state.isValid = true;
      this.state.validated = true;
      return this.state.isValid;
    }

    this.state.isValid = (await validate(this.state.value?.input, this.getValidationRules().join('|'))).valid;

    this.state.validated = true;

    return this.state?.isValid ?? false;
  }

  getValidationRules() {
    const rules = super.getValidationRules();

    if (this.state.ignoreValidation) {
      return [];
    }

    if ([FieldType.EMAIL, FieldType.EMAIL_CONFIRM].includes(this.state.type) && this.state.required) {
      rules.push('email');
    }

    if (this.state.type === FieldType.PHONE_NUMBER && this.state.required) {
      rules.push('phone');
    }

    if (this.state.type === FieldType.EMAIL_CONFIRM) {
      const fieldEmail = this.getSpecificFieldFromInputType<FormElementTextModel>(FieldType.EMAIL);
      const fieldId = fieldEmail?.state.id;
      if (fieldEmail && fieldId) {
        rules.push(`is_same_as:${fieldId}`);
      }
    }
    if (this.state.type === FieldType.EMAIL) {
      const emailConfirmField = this.getSpecificFieldFromInputType<FormElementTextModel>(FieldType.EMAIL_CONFIRM);
      const fieldId = emailConfirmField?.state.id;
      if (emailConfirmField && fieldId) {
        rules.push(`is_same_as:${fieldId}`);
      }
    }
    if (this.state.type === FieldType.PASSWORD_CONFIRM) {
      const fieldPassword = this.getSpecificFieldFromInputType<FormElementTextModel>(FieldType.PASSWORD);
      const fieldId = fieldPassword?.state.id;
      if (fieldPassword && fieldId) {
        rules.push(`is_same_as:${fieldId}`);
      }
    }
    if (this.state.type === FieldType.PASSWORD) {
      const passwordConfirmField = this.getSpecificFieldFromInputType<FormElementTextModel>(FieldType.PASSWORD_CONFIRM);
      const fieldId = passwordConfirmField?.state.id;
      if (passwordConfirmField && fieldId) {
        rules.push(`is_same_as:${fieldId}`);
      }
    }

    return rules;
  }

  getInitialValue(): FormElementTextValue | undefined {
    return this.parseStringValue(this.getInitialStringValue() ?? '');
  }

  parseStringValue(value: string): FormElementTextValue | undefined {
    let countryCode: string | undefined;

    if (this.state.country?.codesEnabled && this.state.country?.codeTags) {
      this.state.country.codeTags.forEach((codeTag) => {
        if (value.startsWith(codeTag)) {
          countryCode = codeTag;
          value = value.replace(codeTag, '');
        }
      });
    }

    if (!value) {
      return undefined;
    }

    return {
      input: value,
      countryCode
    };
  }

  getSerializedPostValue(): string {
    if (this.state.value?.input) {
      return `${this.state.value.countryCode ?? ''}${this.state.value.input}`;
    }

    return '';
  }

  getSerializedCookieValue(): string {
    return this.getSerializedPostValue();
  }

  authorSignature(): string {
    return 'Nicky Christensen';
  }
}
